import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Result, Button, Spin, Typography, Space, Card } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { useAppDispatch } from "../../hooks/redux";
import "./styles.css";
import { clearPaymentData } from "../../store/paymentDetails";

const { Title, Text } = Typography;

const BRAND = "#0576b7";
const SUCCESS = "#1d9c5b";
const FAILED = "#d4380d";

const PaymentSuccessPage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [animationComplete, setAnimationComplete] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      setTimeout(() => setAnimationComplete(true), 100);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleGoHome = () => {
    dispatch(clearPaymentData(""));
    navigate("/");
  };

  const renderLoadingState = () => (
    <div className="payment-result-container">
      <Card className="payment-result-card">
        <Space
          direction="vertical"
          size="large"
          align="center"
          style={{ width: "100%" }}
        >
          <Spin
            indicator={
              <LoadingOutlined
                style={{ fontSize: 48, color: BRAND }}
                spin
              />
            }
            size="large"
          />
          <Title
            level={3}
            style={{
              margin: 0,
              color: "#222",
              fontFamily: "NotoSansMalayalam-Bold",
            }}
          >
            Processing Payment...
          </Title>
          <Text style={{ color: "#666" }}>
            Please wait while we verify your payment status
          </Text>
        </Space>
      </Card>
    </div>
  );

  const renderSuccessState = () => (
    <div className="payment-result-container">
      <Card className="payment-result-card">
        <Result
          icon={
            <CheckCircleOutlined
              className={`payment-success-icon ${
                animationComplete ? "animate" : ""
              }`}
              style={{ fontSize: 80, color: SUCCESS }}
            />
          }
          title={
            <Title
              level={2}
              style={{
                color: SUCCESS,
                marginBottom: 8,
                fontFamily: "NotoSansMalayalam-Bold",
              }}
            >
              Payment Successful!
            </Title>
          }
          subTitle={
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Text style={{ fontSize: 16, color: "#555" }}>
                Thank you for your payment. Your subscription has been activated
                successfully.
              </Text>

              <div className="payment-info-block">
                <Text className="payment-info-text">
                  An email with your subscription details and login password
                  will be sent to your registered email address within the next
                  few minutes.
                </Text>
              </div>
            </Space>
          }
          extra={
            <Space size="middle">
              <Button
                type="primary"
                size="large"
                icon={<HomeOutlined />}
                onClick={handleGoHome}
                style={{ background: BRAND, borderColor: BRAND }}
              >
                Go to Home
              </Button>
            </Space>
          }
        />
      </Card>
    </div>
  );

  const renderFailedState = () => (
    <div className="payment-result-container">
      <Card className="payment-result-card">
        <Result
          icon={
            <CloseCircleOutlined
              className={`payment-failed-icon ${
                animationComplete ? "animate" : ""
              }`}
              style={{ fontSize: 80, color: FAILED }}
            />
          }
          title={
            <Title
              level={2}
              style={{
                color: FAILED,
                marginBottom: 8,
                fontFamily: "NotoSansMalayalam-Bold",
              }}
            >
              Payment Failed
            </Title>
          }
          subTitle={
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Text style={{ fontSize: 16, color: "#555" }}>
                Unfortunately, your payment could not be processed. Please try
                again or contact support.
              </Text>

              <div className="error-details">
                <Text style={{ color: "#666" }}>
                  Common reasons for payment failure:
                </Text>
                <ul>
                  <li>Insufficient funds</li>
                  <li>Card expired or invalid</li>
                  <li>Network connection issues</li>
                  <li>Bank security restrictions</li>
                </ul>
              </div>
            </Space>
          }
          extra={
            <Space size="middle">
              <Button
                type="primary"
                size="large"
                icon={<HomeOutlined />}
                onClick={handleGoHome}
                style={{ background: BRAND, borderColor: BRAND }}
              >
                Go to Home
              </Button>
            </Space>
          }
        />
      </Card>
    </div>
  );

  if (isLoading) {
    return renderLoadingState();
  }

  if (type === "success") {
    return renderSuccessState();
  } else if (type === "failed") {
    return renderFailedState();
  } else {
    navigate("/", { replace: true });
    return null;
  }
};

export default PaymentSuccessPage;
